import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getConceptFilter } from "../../../utils/getConceptFilter";
import { useGetConceptsFiltersMutation } from "../../../store/api/lumi/concepts";
import { debounce } from "lodash";
import { formatDateToUtc } from "../../../utils/formatDate";
import { Spin } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import MetadataShareOfVoiceOverTimeChart from "./MetadataShareOfVoiceOverTimeChart";
import { ScoreLinesFilter } from "../../Common/ScoreLinesFilter";


const MetadataShareOfVoiceOverTime = (props) => {
    const {
        selectedDateName,
        interval,
        id,
        metaDataQu,
        widget,
        smoothLine,
        setAdditionalFilters,
        scoreLines,
        setScoreLines,
    } = props;

    const [activeRequests, setActiveRequests] = useState(0);
    const [chartData, setChartData] = useState([]);
    const [driverValues, setDriverValues] = useState([]);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [loading, setLoading] = useState(true);


    const isLoading = activeRequests > 0;

    useEffect(() => {
        if (metaDataQu?.metaDataDrivers?.length > 0) {
            const filteredDrivers = metaDataQu.metaDataDrivers.filter(
                (driver) => driver.values && driver.values.length > 0,
            );
            setDriverValues(filteredDrivers);

            const defaultDriver = filteredDrivers[0] || {};
            const defaultValue =
                defaultDriver.values && defaultDriver.values.length > 0
                    ? [Number(defaultDriver.values[0].value)]
                    : [];

            setSelectedDriver(defaultDriver.name || null);
            if (scoreLines.length === 0) {

                setScoreLines(() => [
                    {
                        id: 1,
                        selectedValues: defaultValue,
                    },
                ]);
            }
        }
    }, [metaDataQu]);
    const conceptFilter = useMemo(
        () => JSON.stringify(getConceptFilter(props)),
        [props],
    );

    const [getVolume] = useGetConceptsFiltersMutation();

    const fetchAllLinesData = useCallback(async () => {
        if (!selectedDriver || scoreLines.length === 0) return;

        setActiveRequests((prev) => prev + 1);

        try {
            const promises = scoreLines.map((line) => {
                if (line.selectedValues.length === 0) return null;

                const filter = [
                    {
                        name: selectedDriver,
                        values: line.selectedValues,
                    },
                ];

                return getVolume({
                    id,
                    concept_selector: JSON.parse(conceptFilter),
                    filter,
                    breakdowns: [{ name: selectedDateName, interval }],
                }).then(({ data: dataConcepts }) => {
                    const lineData =
                        dataConcepts.breakdowns?.[0]?.buckets.map((bucket) => ({
                            name: selectedDriver,
                            values: line.selectedValues,
                            label: formatDateToUtc(bucket.label, interval),
                            average:
                                bucket.total_count > 0
                                    ? (
                                        (Number(bucket.filter_count) /
                                            Number(bucket.total_count)) *
                                        100
                                    ).toFixed(2)
                                    : 0,
                            lineId: line.id,
                        })) || [];

                    return {
                        lineData,
                        filterCount: dataConcepts.filter_count,
                    };
                });
            });

            const results = await Promise.all(promises.filter((p) => p !== null));

            let combinedData = [];
            let totalFilterCount = 0;

            results.forEach((result) => {
                combinedData.push(...result.lineData);
                totalFilterCount = result.filterCount;
            });

            setChartData(combinedData);
        } catch (error) {
            console.error("Error fetching line data:", error);
        } finally {
            setActiveRequests((prev) => prev - 1);
            setLoading(false)
        }
    }, [
        selectedDriver,
        scoreLines,
        conceptFilter,
        selectedDateName,
        interval,
    ]);

    useEffect(() => {
        setLoading(true)
        const debouncedFetch = debounce(fetchAllLinesData, 1000);
        debouncedFetch();
        return () => {
            debouncedFetch.cancel();
        };
    }, [fetchAllLinesData]);

    const handleDriverChange = (driverName) => {
        setSelectedDriver(driverName);

        const foundDriver = driverValues.find((d) => d.name === driverName);
        const defaultValues =
            foundDriver?.values?.length > 0
                ? [Number(foundDriver.values[0].value)]
                : [];

        setScoreLines((prevLines) =>
            prevLines.map((line) => ({
                ...line,
                selectedValues: defaultValues,
            })),
        );
    };

    const handleValueChange = (lineId, values) => {
        setScoreLines((prevLines) =>
            prevLines.map((l) => {
                if (l.id === lineId) {
                    return {
                        ...l,
                        selectedValues: values.map(Number),
                    };
                }
                return l;
            }),
        );
    };

    const addLine = () => {
        setScoreLines((prevLines) => {
            const newId = prevLines.length > 0 ? prevLines[prevLines.length - 1].id + 1 : 1;
            const foundDriver = driverValues.find((d) => d.name === selectedDriver);
            const defaultValue =
                foundDriver?.values?.length > 0
                    ? [Number(foundDriver.values[0].value)]
                    : [];

            return [
                ...prevLines,
                {
                    id: newId,
                    selectedValues: defaultValue,
                },
            ];
        });
    };

    const removeLine = (lineId) => {
        setScoreLines((prevLines) => prevLines.filter((line) => line.id !== lineId));
    };
    const updateLineLabel = (lineId, newLabel) => {
        setScoreLines((prevLines) =>
            prevLines.map((line) =>
                line.id === lineId
                    ? { ...line, label: newLabel }
                    : line,
            ),
        );
    };

    useEffect(() => {
        setAdditionalFilters?.([
            {
                key: "Special",
                label: "Score Filter",
                showArrow: false,
                children: (
                    <ScoreLinesFilter
                        driverValues={driverValues}
                        selectedDriver={selectedDriver}
                        handleDriverChange={handleDriverChange}
                        scoreLines={scoreLines}
                        handleValueChange={handleValueChange}
                        removeLine={removeLine}
                        addLine={addLine}
                        updateLineLabel={updateLineLabel}
                    />
                ),
            },
        ]);
    }, [scoreLines, driverValues, selectedDriver]);

    if (isLoading || loading) {
        return (
            <Spin
                className="spinner-by-center"
                indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            />
        );
    }

    return (
        <div>
            <MetadataShareOfVoiceOverTimeChart
                chartData={chartData}
                lines={scoreLines}
                widget={widget}
                smoothLine={smoothLine}
            />
        </div>
    );
};

export default MetadataShareOfVoiceOverTime;



